import React, { useState, useEffect, useContext, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	CircularProgress,
	Select,
	InputLabel,
	MenuItem,
	FormControl,
	TextField,
	InputAdornment,
	IconButton,
	FormControlLabel,
	Switch
  } from '@material-ui/core';
  
import { Visibility, VisibilityOff } from '@material-ui/icons';

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import useWhatsApps from "../../hooks/useWhatsApps";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	multFieldLine: {
		display: "flex",
		gap: theme.spacing(2),
		marginBottom: theme.spacing(2),
		"& > *": {
			flex: 1,
		},
	},
	singleFieldLine: {
		marginBottom: theme.spacing(2),
	},
	// ✅ CORREGIDO: FormSection dinámico según tema
	formSection: {
		marginBottom: theme.spacing(3),
		padding: theme.spacing(2),
		borderRadius: '8px',
		backgroundColor: theme.mode === 'dark' ? '#374151' : '#f8f9fa',
		border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e9ecef'}`,
	},
	// ✅ CORREGIDO: SectionTitle dinámico según tema
	sectionTitle: {
		fontSize: '16px',
		fontWeight: 'bold',
		color: theme.palette.text.primary,
		marginBottom: theme.spacing(2),
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
	},
	btnWrapper: {
		position: "relative",
		display: 'flex',
		gap: theme.spacing(2),
		justifyContent: 'flex-end',
		marginTop: theme.spacing(3),
	},
	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
	formControl: {
		margin: theme.spacing(1),
		minWidth: 120,
	},
	maxWidth: {
		width: '100%',
	},
	textField: {
		marginBottom: theme.spacing(2),
	},
	saveButton: {
		backgroundColor: '#1976d2',
		color: 'white',
		borderRadius: '8px',
		padding: '8px 24px',
		fontWeight: 'bold',
		textTransform: 'none',
		boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
		'&:hover': {
			backgroundColor: '#1565c0',
			transform: 'translateY(-1px)',
			boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
		},
	},
	cancelButton: {
		backgroundColor: '#f44336',
		color: 'white',
		borderRadius: '8px',
		padding: '8px 24px',
		fontWeight: 'bold',
		textTransform: 'none',
		boxShadow: '0 2px 8px rgba(244, 67, 54, 0.3)',
		'&:hover': {
			backgroundColor: '#d32f2f',
			transform: 'translateY(-1px)',
			boxShadow: '0 4px 12px rgba(244, 67, 54, 0.4)',
		},
	},
	switchContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		marginTop: theme.spacing(1),
	},
	// ✅ CORREGIDO: SwitchLabel dinámico según tema
	switchLabel: {
		fontSize: '14px',
		fontWeight: '500',
		color: theme.palette.text.primary,
	},
	// ✅ CORREGIDO: TextField dinámico según tema
	textFieldDynamic: {
		'& .MuiOutlinedInput-root': {
			backgroundColor: theme.mode === 'dark' ? '#374151' : theme.palette.background.paper,
			color: theme.palette.text.primary,
			'& fieldset': {
				borderColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
			},
			'&:hover fieldset': {
				borderColor: theme.palette.primary.main,
			},
			'&.Mui-focused fieldset': {
				borderColor: theme.palette.primary.main,
			},
		},
		'& .MuiInputLabel-root': {
			color: theme.palette.text.secondary || theme.palette.text.primary,
		},
		'& .MuiInputLabel-root.Mui-focused': {
			color: theme.palette.primary.main,
		},
		'& .MuiFormHelperText-root': {
			color: theme.palette.text.secondary || theme.palette.text.primary,
		},
	},
	// ✅ CORREGIDO: FormControl dinámico según tema
	formControlDynamic: {
		'& .MuiOutlinedInput-root': {
			backgroundColor: theme.mode === 'dark' ? '#374151' : theme.palette.background.paper,
			color: theme.palette.text.primary,
			'& fieldset': {
				borderColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
			},
			'&:hover fieldset': {
				borderColor: theme.palette.primary.main,
			},
			'&.Mui-focused fieldset': {
				borderColor: theme.palette.primary.main,
			},
		},
		'& .MuiInputLabel-root': {
			color: theme.palette.text.secondary || theme.palette.text.primary,
		},
		'& .MuiMenuItem-root': {
			backgroundColor: theme.palette.background.paper,
			color: theme.palette.text.primary,
			'&:hover': {
				backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#f5f5f5',
			},
		},
	},
	// ✅ CORREGIDO: HelperText dinámico según tema
	helperText: {
		fontSize: '12px',
		color: theme.palette.text.secondary || theme.palette.text.primary,
	},
}));

const UserSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Too Short!")
		.max(50, "Too Long!")
		.required("Required"),
	password: Yup.string().min(5, "Too Short!").max(50, "Too Long!"),
	email: Yup.string().email("Invalid email").required("Required"),
});

const UserModal = ({ open, onClose, userId }) => {
	const classes = useStyles();

	const initialState = {
		name: "",
		email: "",
		password: "",
		profile: "user",
		allTicket: "disabled"
	};

	const { user: loggedInUser } = useContext(AuthContext);

	const [user, setUser] = useState(initialState);
	const [selectedQueueIds, setSelectedQueueIds] = useState([]);
	const [whatsappId, setWhatsappId] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [allowGroup, setAllowGroup] = useState(false); // ✅ NUEVA OPCIÓN PARA GRUPOS
	const { loading, whatsApps } = useWhatsApps();
	const isMounted = useRef(true);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		const fetchUser = async () => {
			if (!userId) {
				// ✅ LIMPIAR VALORES RESIDUALES AL CREAR NUEVO USUARIO
				if (isMounted.current) {
					setUser({
						name: "",
						email: "",
						password: "",
						profile: "user",
						allTicket: "disabled"
					});
					setSelectedQueueIds([]);
					setWhatsappId('');
					setShowPassword(false);
					setAllowGroup(false);
				}
				return;
			}
			try {
				const { data } = await api.get(`/users/${userId}`);
				if (isMounted.current) {
					setUser(prevState => {
						return { ...prevState, ...data };
					});
					const userQueueIds = data.queues?.map(queue => queue.id);
					setSelectedQueueIds(userQueueIds || []);
					setWhatsappId(data.whatsappId ? data.whatsappId : '');
					setAllowGroup(data.allowGroup || false); // ✅ CARGAR OPCIÓN DE GRUPOS
				}
			} catch (err) {
				if (isMounted.current) {
					toastError(err);
				}
			}
		};

		if (isMounted.current) {
			fetchUser();
		}
	}, [userId, open]); // eslint-disable-line react-hooks/exhaustive-deps

	const handleClose = () => {
		// ✅ LIMPIAR COMPLETAMENTE EL FORMULARIO
		setUser({
			name: "",
			email: "",
			password: "",
			profile: "user",
			allTicket: "disabled"
		});
		setSelectedQueueIds([]);
		setWhatsappId('');
		setShowPassword(false);
		setAllowGroup(false);
		onClose();
	};

	const handleSaveUser = async values => {
		const userData = { ...values, whatsappId, queueIds: selectedQueueIds, allTicket: values.allTicket, allowGroup };
		try {
			if (userId) {
				await api.put(`/users/${userId}`, userData);
			} else {
				await api.post("/users", userData);
			}
			toast.success(i18n.t("userModal.success"));
		} catch (err) {
			toastError(err);
		}
		handleClose();
	};

	return (
		<div className={classes.root}>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="xs"
				fullWidth
				scroll="paper"
			>
				<DialogTitle id="form-dialog-title">
					{userId
						? `${i18n.t("userModal.title.edit")}`
						: `${i18n.t("userModal.title.add")}`}
				</DialogTitle>
				<Formik
					initialValues={user}
					enableReinitialize={true}
					validationSchema={UserSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveUser(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ touched, errors, isSubmitting, values, setFieldValue }) => (
						<Form>
							<DialogContent dividers>
								{/* ✅ SECCIÓN: INFORMACIÓN BÁSICA */}
								<div className={classes.formSection}>
									<div className={classes.sectionTitle}>
										{/* eslint-disable-next-line jsx-a11y/accessible-emoji */}
										👤 Información Básica
									</div>
									<div className={classes.multFieldLine}>
										<TextField
											label={i18n.t("userModal.form.name")}
											autoFocus
											name="name"
											value={values.name || ""}
											onChange={(e) => setFieldValue('name', e.target.value)}
											error={touched.name && Boolean(errors.name)}
											helperText={touched.name && errors.name}
											variant="outlined"
											margin="dense"
											fullWidth
											className={classes.textFieldDynamic}
										/>
										<TextField
											name="password"
											variant="outlined"
											margin="dense"
											fullWidth
											label={i18n.t("userModal.form.password")}
											value={values.password || ""}
											onChange={(e) => setFieldValue('password', e.target.value)}
											error={touched.password && Boolean(errors.password)}
											helperText={touched.password && errors.password}
											type={showPassword ? 'text' : 'password'}
											className={classes.textFieldDynamic}
											InputProps={{
											endAdornment: (
												<InputAdornment position="end">
												<IconButton
													aria-label="toggle password visibility"
													onClick={() => setShowPassword((e) => !e)}
												>
													{showPassword ? <VisibilityOff /> : <Visibility />}
												</IconButton>
												</InputAdornment>
											)
											}}
										/>
									</div>
									<div className={classes.multFieldLine}>
										<TextField
											label={i18n.t("userModal.form.email")}
											name="email"
											value={values.email || ""}
											onChange={(e) => setFieldValue('email', e.target.value)}
											error={touched.email && Boolean(errors.email)}
											helperText={touched.email && errors.email}
											variant="outlined"
											margin="dense"
											fullWidth
											className={classes.textFieldDynamic}
										/>
										<FormControl
											variant="outlined"
											className={`${classes.formControl} ${classes.formControlDynamic}`}
											margin="dense"
										>
											<Can
												role={loggedInUser.profile}
												perform="user-modal:editProfile"
												yes={() => (
													<>
														<InputLabel id="profile-selection-input-label">
															{i18n.t("userModal.form.profile")}
														</InputLabel>

														<Field
															as={Select}
															label={i18n.t("userModal.form.profile")}
															name="profile"
															labelId="profile-selection-label"
															id="profile-selection"
															required
														>
															<MenuItem value="admin">Admin</MenuItem>
															<MenuItem value="user">User</MenuItem>
														</Field>
													</>
												)}
											/>
										</FormControl>
									</div>
								</div>
								<Can
									role={loggedInUser.profile}
									perform="user-modal:editQueues"
									yes={() => (
										<QueueSelect
											selectedQueueIds={selectedQueueIds}
											onChange={values => setSelectedQueueIds(values)}
										/>
									)}
								/>
								<Can
									role={loggedInUser.profile}
									perform="user-modal:editProfile"
									yes={() => (
										<FormControl variant="outlined" margin="dense" className={`${classes.maxWidth} ${classes.formControlDynamic}`} fullWidth>
											<InputLabel>
												{i18n.t("userModal.form.whatsapp")}
											</InputLabel>
											<Field
												as={Select}
												value={whatsappId}
												onChange={(e) => setWhatsappId(e.target.value)}
												label={i18n.t("userModal.form.whatsapp")}
											>
												<MenuItem value={''}>
													<div style={{ display: 'flex', alignItems: 'center' }}>
														<span style={{ marginRight: '8px' }} role="img" aria-label="Prohibido">❌</span>
														<span>Ninguna</span>
													</div>
												</MenuItem>
												{whatsApps.map((whatsapp) => (
													<MenuItem key={whatsapp.id} value={whatsapp.id}>
														<div style={{ display: 'flex', alignItems: 'center' }}>
															<span style={{ marginRight: '8px', fontSize: '16px' }} role="img" aria-label="WhatsApp">📱</span>
															<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
																<span style={{ fontWeight: 'bold', fontSize: '14px' }}>
																	{whatsapp.name || `ID: ${whatsapp.id}`}
																</span>
																<span style={{ fontSize: '12px', color: '#666' }}>
																	{whatsapp.waName ? `${whatsapp.name} - ${whatsapp.waName}` : 
																	 whatsapp.name ? whatsapp.name : 
																	 `Conexión ${whatsapp.id}`}
																</span>
															</div>
														</div>
													</MenuItem>
												))}
											</Field>
										</FormControl>
									)}
								/>										
								<Can
									role={loggedInUser.profile}
									perform="user-modal:editProfile"
									yes={() => (!loading &&
										<div className={classes.textField}>
											<FormControl
												variant="outlined"
												className={`${classes.maxWidth} ${classes.formControlDynamic}`}
												margin="dense"
												fullWidth
											>
												<>
													<InputLabel id="profile-selection-input-label">
														Ver Tickets Sin Asignar
													</InputLabel>

													<Field
														as={Select}
														label="Ver Tickets Sin Asignar"
														name="allTicket"
														labelId="allTicket-selection-label"
														id="allTicket-selection"
														required
													>
														<MenuItem value="enabled">Habilitado</MenuItem>
														<MenuItem value="disabled">Deshabilitado</MenuItem>
													</Field>
												</>
											</FormControl>
											<div className={classes.helperText} style={{ marginTop: '4px', padding: '0 8px' }}>
												<strong>Habilitado:</strong> Puede ver tickets que no están asignados a ningún departamento<br/>
												<strong>Deshabilitado:</strong> Solo ve tickets de sus departamentos asignados
											</div>
										</div>

									)}
								/>
								{/* ✅ NUEVA OPCIÓN: PERMITIR GRUPOS */}
								<Can
									role={loggedInUser.profile}
									perform="user-modal:editProfile"
									yes={() => (
										<FormControlLabel
											control={
												<Switch
													checked={allowGroup}
													onChange={(e) => setAllowGroup(e.target.checked)}
													name="allowGroup"
													color="primary"
												/>
											}
											label="Permitir Grupos de WhatsApp"
											style={{ marginTop: 16 }}
										/>
									)}
								/>
								
							</DialogContent>
							<DialogActions className={classes.btnWrapper}>
								<Button
									onClick={handleClose}
									disabled={isSubmitting}
									className={classes.cancelButton}
								>
									{i18n.t("userModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									disabled={isSubmitting}
									className={classes.saveButton}
								>
									{userId
										? `${i18n.t("userModal.buttons.okEdit")}`
										: `${i18n.t("userModal.buttons.okAdd")}`}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default UserModal;
